import React, { useMemo, useState, useCallback } from 'react';
import { Tables } from '../types/supabase';

type Route = Tables<'route'>;

interface ElevationProfileProps {
  route: Route | null;
  onProfileHover?: (position: { distance: number; elevation: number; coordinates: [number, number]; grade?: number } | null) => void;
  mapHoverPosition?: { distance: number; elevation: number; coordinates: [number, number]; grade?: number } | null;
}

const ElevationProfile: React.FC<ElevationProfileProps> = ({ 
  route, 
  onProfileHover, 
  mapHoverPosition 
}) => {
  const [profileHoverPosition, setProfileHoverPosition] = useState<{ distance: number; elevation: number; coordinates: [number, number]; grade?: number } | null>(null);

  const elevationData = useMemo(() => {
    if (!route?.path) return null;

    try {
      const routePath = route.path as any;
      if (!routePath.coordinates || routePath.coordinates.length === 0) return null;

      // Extract coordinates and calculate cumulative distance
      const coordinates = routePath.coordinates as [number, number, number][];
      const points: Array<{
        distance: number;
        elevation: number;
        cumulativeDistance: number;
        coordinates: [number, number, number];
        grade?: number; // Add grade calculation
      }> = [];

      let cumulativeDistance = 0;
      let previousPoint: [number, number, number] | null = null;

      coordinates.forEach((coord, index) => {
        const [lng, lat, elevation] = coord;
        
        if (previousPoint) {
          // Calculate distance between points using Haversine formula
          const [prevLng, prevLat] = previousPoint;
          const R = 6371000; // Earth's radius in meters
          const dLat = (lat - prevLat) * Math.PI / 180;
          const dLng = (lng - prevLng) * Math.PI / 180;
          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                    Math.cos(prevLat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
                    Math.sin(dLng / 2) * Math.sin(dLng / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distance = R * c;
          
          cumulativeDistance += distance;
        }

        points.push({
          distance: 0, // Individual segment distance
          elevation: elevation || 0,
          cumulativeDistance: cumulativeDistance,
          coordinates: coord
        });

        previousPoint = coord;
      });

      // Calculate rolling grade with 100m window
      points.forEach((point, index) => {
        if (index === 0) {
          point.grade = 0;
          return;
        }

        // Find points within 100m window (50m before and after current point)
        const windowStart = Math.max(0, point.cumulativeDistance - 50);
        const windowEnd = point.cumulativeDistance + 50;
        
        let windowPoints = points.filter(p => 
          p.cumulativeDistance >= windowStart && 
          p.cumulativeDistance <= windowEnd
        );

        if (windowPoints.length < 2) {
          point.grade = 0;
          return;
        }

        // Calculate grade as (elevation change / distance) * 100
        const firstPoint = windowPoints[0];
        const lastPoint = windowPoints[windowPoints.length - 1];
        const elevationChange = lastPoint.elevation - firstPoint.elevation;
        const distanceChange = lastPoint.cumulativeDistance - firstPoint.cumulativeDistance;
        
        if (distanceChange > 0) {
          point.grade = (elevationChange / distanceChange) * 100;
        } else {
          point.grade = 0;
        }
      });

      return {
        points,
        totalDistance: cumulativeDistance,
        minElevation: Math.min(...points.map(p => p.elevation)),
        maxElevation: Math.max(...points.map(p => p.elevation)),
        elevationGain: route.elevation_gain || 0
      };
    } catch (error) {
      console.error('Error parsing elevation data:', error);
      return null;
    }
  }, [route]);

  // Handle profile hover
  const handleProfileHover = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!elevationData || !onProfileHover) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const xRatio = x / rect.width;
    
    // Find the closest point based on x position
    const targetDistance = xRatio * elevationData.totalDistance;
    let closestPoint = elevationData.points[0];
    let minDistanceDiff = Math.abs(closestPoint.cumulativeDistance - targetDistance);

    for (const point of elevationData.points) {
      const distanceDiff = Math.abs(point.cumulativeDistance - targetDistance);
      if (distanceDiff < minDistanceDiff) {
        minDistanceDiff = distanceDiff;
        closestPoint = point;
      }
    }

    const positionData = {
      distance: closestPoint.cumulativeDistance,
      elevation: closestPoint.elevation,
      coordinates: [closestPoint.coordinates[0], closestPoint.coordinates[1]] as [number, number],
      grade: closestPoint.grade
    };

    console.log('Profile hover:', positionData);
    setProfileHoverPosition(positionData);
    onProfileHover(positionData);
  }, [elevationData, onProfileHover]);

  // Handle profile leave
  const handleProfileLeave = useCallback(() => {
    console.log('Profile leave');
    setProfileHoverPosition(null);
    if (onProfileHover) {
      onProfileHover(null);
    }
  }, [onProfileHover]);

  if (!elevationData) {
    return (
      <div className="elevation-profile">
        <h3>Elevation Profile</h3>
        <div className="elevation-placeholder">
          <p>No elevation data available for this route.</p>
        </div>
      </div>
    );
  }

  const { points, totalDistance, minElevation, maxElevation, elevationGain } = elevationData;
  const elevationRange = maxElevation - minElevation;
  const chartHeight = 200;
  const chartWidth = 100; // Keep at 100 for viewBox, but make SVG responsive

  // Calculate SVG path for the elevation profile - ensure continuous line and proper bounds
  const svgPath = points.map((point, index) => {
    const x = (point.cumulativeDistance / totalDistance) * chartWidth;
    
    // Ensure y-coordinate stays within chart bounds (0 to chartHeight)
    let y = chartHeight - ((point.elevation - minElevation) / elevationRange) * chartHeight;
    
    // Clamp y values to prevent going outside chart bounds
    y = Math.max(0, Math.min(chartHeight, y));
    
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  // Calculate fill path that ensures it stays within bounds
  const fillPath = `${svgPath} L ${chartWidth} ${chartHeight} L 0 ${chartHeight} Z`;

  // Determine which position to show (map hover takes precedence for display, but profile hover controls the profile tracker)
  const activePosition = mapHoverPosition || profileHoverPosition;
  const profileTrackerPosition = profileHoverPosition; // Always use profile hover for profile tracker

  return (
    <div className="elevation-profile">
      <div className="elevation-chart">
        <svg 
          width="100%" 
          height={chartHeight} 
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="none"
          className="elevation-svg"
          onMouseMove={handleProfileHover}
          onMouseLeave={handleProfileLeave}
          style={{ cursor: 'crosshair' }}
        >
          {/* Clean background - no grid */}
          <rect width="100%" height="100%" fill="transparent" />
          
          {/* Elevation line - ensure it's continuous */}
          <path
            d={svgPath}
            fill="none"
            stroke="#FF8C00"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          
          {/* Fill area below the line */}
          <path
            d={fillPath}
            fill="url(#elevationGradient)"
            opacity="0.2"
          />
          
          {/* Active position indicator - much slimmer tracker */}
          {profileTrackerPosition && (
            <g className="position-indicator">
              {/* Vertical line - slim tracker */}
              <line
                x1={(profileTrackerPosition.distance / totalDistance) * chartWidth}
                y1="0"
                x2={(profileTrackerPosition.distance / totalDistance) * chartWidth}
                y2={chartHeight}
                stroke="#FF0000"
                strokeWidth="1"
                strokeDasharray="3,3"
                opacity="0.8"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          )}
          
          {/* Gradient definition */}
          <defs>
            <linearGradient id="elevationGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#FF8C00" stopOpacity="0.8"/>
              <stop offset="100%" stopColor="#FF8C00" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
        </svg>
        
        {/* Distance markers - X-axis (now below the chart) */}
        <div className="distance-markers">
          {(() => {
            const totalKm = totalDistance / 1000;
            let interval: number;
            
            // Determine tick interval based on route length
            if (totalKm <= 10) {
              interval = 1; // Every 1km for routes ≤10km
            } else if (totalKm <= 50) {
              interval = 2; // Every 2km for routes 10-50km
            } else if (totalKm <= 100) {
              interval = 5; // Every 5km for routes 50-100km
            } else {
              interval = 10; // Every 10km for routes >100km
            }
            
            // Generate ticks at the determined interval
            const ticks: number[] = [];
            for (let km = 0; km <= Math.ceil(totalKm); km += interval) {
              if (km <= totalKm) {
                ticks.push(km);
              }
            }
            
            return ticks.map(km => (
              <div 
                key={km} 
                className="distance-marker" 
                style={{ left: `${(km * 1000 / totalDistance) * 100}%` }}
              >
                <span className="marker-label">{km} km</span>
              </div>
            ));
          })()}
        </div>
        
        {/* Elevation markers - Y-axis */}
        <div className="elevation-markers">
          {(() => {
            const elevationRange = maxElevation - minElevation;
            let interval: number;
            
            // Determine tick interval based on elevation range
            if (elevationRange <= 200) {
              interval = 25; // Every 25m for ranges ≤200m
            } else if (elevationRange <= 500) {
              interval = 50; // Every 50m for ranges 200-500m
            } else if (elevationRange <= 1000) {
              interval = 100; // Every 100m for ranges 500-1000m
            } else if (elevationRange <= 2000) {
              interval = 200; // Every 200m for ranges 1000-2000m
            } else {
              interval = 500; // Every 500m for ranges >2000m
            }
            
            // Generate ticks at the determined interval, starting from rounded min elevation
            const startElevation = Math.floor(minElevation / interval) * interval;
            const endElevation = Math.ceil(maxElevation / interval) * interval;
            const ticks: number[] = [];
            
            for (let elevation = startElevation; elevation <= endElevation; elevation += interval) {
              if (elevation >= minElevation && elevation <= maxElevation) {
                ticks.push(elevation);
              }
            }
            
            return ticks.map(elevation => (
              <div 
                key={elevation} 
                className="elevation-marker" 
                style={{ bottom: `${((elevation - minElevation) / elevationRange) * 100}%` }}
              >
                <span className="marker-label">{elevation} m</span>
              </div>
            ));
          })()}
        </div>

        {/* Active position info */}
        {profileTrackerPosition && (
          <div className="position-info">
            <div className="position-stat">
              <span className="position-label">Distance:</span>
              <span className="position-value">{(profileTrackerPosition.distance / 1000).toFixed(1)} km</span>
            </div>
            <div className="position-stat">
              <span className="position-label">Elevation:</span>
              <span className="position-value">{Math.round(profileTrackerPosition.elevation)} m</span>
            </div>
            {profileTrackerPosition.grade !== undefined && (
              <div className="position-stat">
                <span className="position-label">Grade:</span>
                <span className="position-value">{profileTrackerPosition.grade.toFixed(1)}%</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ElevationProfile; 